// src/features/executive/performance/widgets/KeyProceduresCard.tsx
import {
  LinearProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { pushDrill } from 'app/slice/executive.slice'
import WidgetCard from 'features/executive/components/WidgetCard/WidgetCard'
import { useKeyProcedures } from '../hooks/use-key-procedures'
import { achievementColor, formatCurrency } from 'utils/executive.utils'

const colorToToken = {
  success: 'success',
  warning: 'warning',
  error: 'error',
  default: 'primary',
} as const

export default function KeyProceduresCard() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { data, loading, error, refetch } = useKeyProcedures(10)

  const title = t('EXECUTIVE.PERFORMANCE.KEY_PROCEDURES.TITLE')
  const isEmpty = !data || data.items.length === 0
  const handleCardClick = () => dispatch(pushDrill({ id: 'key-procedures', label: title }))

  return (
    <WidgetCard
      title={title}
      loading={loading}
      error={error}
      empty={isEmpty}
      onRetry={refetch}
      onClick={handleCardClick}
      skeletonHeight={320}
    >
      {data && (
        <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('EXECUTIVE.PERFORMANCE.KEY_PROCEDURES.PROCEDURE')}</TableCell>
              <TableCell align="right">{t('EXECUTIVE.PERFORMANCE.KEY_PROCEDURES.CASES')}</TableCell>
              <TableCell align="right">{t('EXECUTIVE.PERFORMANCE.KEY_PROCEDURES.TARGET')}</TableCell>
              <TableCell align="right">{t('EXECUTIVE.PERFORMANCE.KEY_PROCEDURES.REVENUE')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.items.map((row) => {
              const pct =
                row.caseTarget != null && row.caseTarget > 0
                  ? (row.caseCount / row.caseTarget) * 100
                  : null
              const token = colorToToken[achievementColor(pct)]
              return (
                <TableRow key={row.procedureId}>
                  <TableCell>{row.procedureName}</TableCell>
                  <TableCell align="right">
                    {row.caseCount.toLocaleString('th-TH')}
                    {pct != null && (
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, pct)}
                        color={token}
                        sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {row.caseTarget ?? '—'}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(row.revenue)}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
        </TableContainer>
      )}
    </WidgetCard>
  )
}
